import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { useUser } from '../context/UserContext';
import { useChat } from '../context/ChatContext';
import { useAd } from '../context/AdContext';
import { Card, SectionHeader, Badge } from '../components/UI';
import { fmt } from '../utils/formatters';

function QuickAction({ emoji, label, onClick, color }) {
    return (
        <button onClick={onClick} style={{
            width: 'calc(50% - 6px)', backgroundColor: 'var(--bg-card)',
            borderRadius: 14, padding: 16, border: `1px solid ${color || 'var(--border)'}`,
            cursor: 'pointer', display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 6, transition: 'border-color 0.15s',
        }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand-400)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = color || 'var(--border)'}
        >
            <span style={{ fontSize: 28 }}>{emoji}</span>
            <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>{label}</span>
        </button>
    );
}

export default function HomeScreen() {
    const navigate = useNavigate();
    const { company, clearCompany } = useCompany();
    const { profile } = useUser();
    const { greet } = useChat();
    const { maybeShowAd } = useAd();

    useEffect(() => {
        greet();
        const t = setTimeout(() => maybeShowAd('dashboard'), 3000);
        return () => clearTimeout(t);
    }, []);

    const persona = company?.persona || {};
    const agentName = persona.name || 'GAAF';
    const greeting = profile.name ? `Hi, ${profile.name.split(' ')[0]} 👋` : 'Welcome 👋';

    return (
        <div style={{ padding: 24, paddingBottom: 16 }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 2 }}>{greeting}</p>
                    <h1 style={{
                        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800,
                        color: 'var(--text-primary)'
                    }}>{company?.companyName}</h1>
                </div>
                <button onClick={() => navigate('/profile')} style={{
                    width: 44, height: 44, borderRadius: '50%', backgroundColor: 'var(--bg-surface)',
                    border: '1px solid var(--border)', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    color: 'var(--brand-300)', fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)',
                }}>
                    {profile.name ? fmt.initials(profile.name) : '👤'}
                </button>
            </div>

            {/* Agent hero */}
            <div style={{
                backgroundColor: 'var(--bg-card)', borderRadius: 20, padding: 16,
                border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
                gap: 12, marginBottom: 16,
            }}>
                <div style={{
                    width: 48, height: 48, borderRadius: '50%', backgroundColor: 'var(--bg-surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0
                }}>🤖</div>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
                        color: 'var(--text-primary)'
                    }}>{agentName}</p>
                    <p style={{ color: 'var(--brand-300)', fontSize: 12 }}>AI Agent · Online</p>
                </div>
                <button onClick={() => { maybeShowAd('calls'); navigate('/chat'); }} style={{
                    backgroundColor: 'var(--brand-500)', color: '#fff', border: 'none',
                    borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 700,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                }}>💬 Chat</button>
            </div>

            {/* Quick actions */}
            <SectionHeader title="What would you like to do?" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <QuickAction emoji="📅" label="Book Appointment" color="var(--brand-700)"
                    onClick={() => { maybeShowAd('appointments'); navigate('/services'); }} />
                <QuickAction emoji="🛍" label="Order" color="var(--brand-700)"
                    onClick={() => { maybeShowAd('orders'); navigate('/services'); }} />
                <QuickAction emoji="🎟" label="Events" color="var(--brand-700)"
                    onClick={() => { maybeShowAd('tickets'); navigate('/events'); }} />
                <QuickAction emoji="✈️" label="Flights" color="var(--brand-700)"
                    onClick={() => { maybeShowAd('tickets'); navigate('/flights'); }} />
            </div>

            {/* Company info */}
            {company && (
                <>
                    <SectionHeader title="Business Info" />
                    <Card>
                        {company.address && (
                            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                <span>📍</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{company.address}</span>
                            </div>
                        )}
                        {company.phone && (
                            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                                <span>📞</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{fmt.phone(company.phone)}</span>
                            </div>
                        )}
                        {company.businessHours && (
                            <div style={{ display: 'flex', gap: 10 }}>
                                <span>🕐</span>
                                <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                                    {Object.entries(company.businessHours).slice(0, 1)
                                        .map(([, h]) => `${h.open} – ${h.close}`).join('')}
                                </span>
                            </div>
                        )}
                    </Card>
                </>
            )}

            {/* Switch */}
            <button onClick={clearCompany} style={{
                display: 'block', margin: '8px auto 0', background: 'none', border: 'none',
                color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', padding: '8px 16px',
            }}>Switch Company →</button>
        </div>
    );
}
